import { useState } from 'react';
import { trpc } from '../lib/trpc.js';
import { formatDateTime } from '../lib/format.js';

function BriefTypeButton({ label, onClick, loading }: { label: string; onClick: () => void; loading: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="btn-primary disabled:opacity-50"
    >
      {loading ? 'Generating…' : label}
    </button>
  );
}

function ResponsePanel({ response, type }: { response: string; type: string }) {
  return (
    <div className="bg-cream-tint border border-gold/30 rounded-lg p-5">
      <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wider">{type.replace(/_/g, ' ')}</p>
      <div className="prose prose-sm max-w-none text-navy whitespace-pre-wrap font-body text-sm leading-relaxed">
        {response}
      </div>
    </div>
  );
}

export function AdvisorPage() {
  const [activeResponse, setActiveResponse] = useState<{ text: string; type: string } | null>(null);
  const [loadingType, setLoadingType] = useState<string | null>(null);
  const [concept, setConcept] = useState('');
  const [footage, setFootage] = useState('');
  const [showCreativeForm, setShowCreativeForm] = useState(false);

  const history = trpc.advisor.history.useQuery({ limit: 10 });

  const dailyBrief = trpc.advisor.generateDailyBrief.useMutation({
    onMutate: () => setLoadingType('daily_brief'),
    onSuccess: (d) => { setActiveResponse({ text: d.response, type: 'Daily Brief' }); setLoadingType(null); history.refetch(); },
    onError: () => setLoadingType(null),
  });

  const weeklyReview = trpc.advisor.generateWeeklyReview.useMutation({
    onMutate: () => setLoadingType('weekly_review'),
    onSuccess: (d) => { setActiveResponse({ text: d.response, type: 'Weekly Review' }); setLoadingType(null); history.refetch(); },
    onError: () => setLoadingType(null),
  });

  const creativeBrief = trpc.advisor.generateCreativeBrief.useMutation({
    onMutate: () => setLoadingType('creative_brief'),
    onSuccess: (d) => { setActiveResponse({ text: d.response, type: 'Creative Brief' }); setLoadingType(null); history.refetch(); },
    onError: () => setLoadingType(null),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl text-navy">AI Advisor</h1>
        <p className="text-xs text-gray-500">Powered by Claude — all sessions saved</p>
      </div>

      <div className="card">
        <h2 className="font-display text-xl text-navy mb-4">Generate</h2>
        <div className="flex flex-wrap gap-3">
          <BriefTypeButton
            label="Daily Brief"
            onClick={() => dailyBrief.mutate()}
            loading={loadingType === 'daily_brief'}
          />
          <BriefTypeButton
            label="Weekly Review"
            onClick={() => weeklyReview.mutate()}
            loading={loadingType === 'weekly_review'}
          />
          <button
            onClick={() => setShowCreativeForm(!showCreativeForm)}
            className="btn-secondary"
          >
            Creative Brief
          </button>
        </div>

        {showCreativeForm && (
          <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Concept</label>
              <input
                type="text"
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                placeholder="e.g. Before/after on a heavily stained sofa"
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Available footage</label>
              <textarea
                value={footage}
                onChange={(e) => setFootage(e.target.value)}
                placeholder="Describe what footage you have available..."
                rows={3}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy resize-none"
              />
            </div>
            <button
              onClick={() => creativeBrief.mutate({ concept, footageDescription: footage })}
              disabled={!concept || !footage || loadingType === 'creative_brief'}
              className="btn-gold disabled:opacity-50"
            >
              {loadingType === 'creative_brief' ? 'Generating…' : 'Generate Creative Brief'}
            </button>
          </div>
        )}
      </div>

      {activeResponse && (
        <ResponsePanel response={activeResponse.text} type={activeResponse.type} />
      )}

      {history.data && history.data.length > 0 && (
        <section className="card">
          <h2 className="font-display text-xl text-navy mb-4">Session History</h2>
          <div className="space-y-3">
            {history.data.map((s: { id: string; type: string; response: string; createdAt: Date | string }) => (
              <div
                key={s.id}
                className="border border-gray-100 rounded-lg p-3 hover:bg-cream-tint cursor-pointer transition-colors"
                onClick={() => setActiveResponse({ text: s.response, type: s.type })}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-navy capitalize">{s.type.replace(/_/g, ' ')}</span>
                  <span className="text-xs text-gray-400">{formatDateTime(s.createdAt)}</span>
                </div>
                <p className="text-xs text-gray-500 line-clamp-2">{s.response.slice(0, 120)}…</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
